/**
 * @module core/validations
 */
import zxcvbn from 'zxcvbn';
import { isDigitString } from '../util';

// /////////////////////////////////////////////////////////
//
// CEP
//
// /////////////////////////////////////////////////////////

/**
 * Retorna **true** se a `string` fornecida representa um CEP
 * válido.
 *
 * @param {String} cep CEP sem formatação (somente os dígitos).
 * @returns {Boolean} **true** se a `string` fornecida representa um CEP válido.
 */
const isValidCEP = (cep) => isDigitString(cep) && cep.length === 8;

// /////////////////////////////////////////////////////////
//
// CPF
//
// /////////////////////////////////////////////////////////

const fakeCPFs = {
  '00000000000': true,
  11111111111: true,
  22222222222: true,
  33333333333: true,
  44444444444: true,
  55555555555: true,
  66666666666: true,
  77777777777: true,
  88888888888: true,
  99999999999: true,
};

/**
 * Retorna **true** se a `string` fornecida representa um CPF
 * válido.
 *
 * @param {String} cpf CPF sem formatação (somente os dígitos).
 * @returns {Boolean} **true** se a `string` fornecida representa um CPF válido.
 */
function isValidCPF(cpf) {
  let sum;
  let remainder;

  sum = 0;
  const cond = cpf && cpf.length === 11 && !fakeCPFs[cpf];
  if (!cond) {
    return false;
  }

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== parseInt(cpf.substring(9, 10), 10)) {
    return false;
  }

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
  }
  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  if (remainder !== parseInt(cpf.substring(10, 11), 10)) {
    return false;
  }
  return true;
}

// /////////////////////////////////////////////////////////
//
// CNPJ
//
// /////////////////////////////////////////////////////////

const fakeCNPJs = {
  '00000000000000': true,
  11111111111111: true,
  22222222222222: true,
  33333333333333: true,
  44444444444444: true,
  55555555555555: true,
  66666666666666: true,
  77777777777777: true,
  88888888888888: true,
  99999999999999: true,
};

/**
 * Retorna **true** se a `string` fornecida representa um CNPJ
 * válido.
 *
 * @param {String} cnpj CNPJ sem formatação (somente os dígitos).
 * @returns {Boolean} **true** se a `string` fornecida representa CNPJ válido.
 */
function isValidCNPJ(cnpj) {
  let result;
  let sum;

  const cond = cnpj && cnpj.length === 14 && !fakeCNPJs[cnpj];
  if (!cond) {
    return false;
  }

  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  sum = 0;
  let pos = size - 7;

  // Valida primeiro dígito verificador
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos;
    pos -= 1;
    if (pos < 2) {
      pos = 9;
    }
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (result !== parseInt(digits.charAt(0), 10)) {
    return false;
  }

  // Valida segundo dígito verificador
  size += 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos;
    pos -= 1;
    if (pos < 2) {
      pos = 9;
    }
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (result !== parseInt(digits.charAt(1), 10)) {
    return false;
  }
  return true;
}

// /////////////////////////////////////////////////////////
//
// Senha
//
// /////////////////////////////////////////////////////////

/**
 * Retorna **true** se a senha fornecida for válida. Para tanto,
 * a senha tem que ter no mínimo seis caracteres e ter uma
 * [força maior que seis](https://github.com/dropbox/zxcvbn).
 *
 * @param {String} password senha a ser analizada.
 * @returns {Boolean} **true** se a senha fornecida é válida.
 */
function isValidPassword(password) {
  return password && password.length >= 6 && zxcvbn(password).score > 1;
}

// /////////////////////////////////////////////////////////
//
// E-mail
//
// /////////////////////////////////////////////////////////

/**
 * Retorna **true** se o email fornecida for válido.
 *
 * @param {String} email eamil a ser analizado.
 * @returns {Boolean} **true** se o email fornecido é válido.
 */
function isValidEmail(email) {
  const re = new RegExp(
    [
      "^(?:[a-z0-9!#$%&amp;'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&amp;'*+/=?^",
      '_`{|}~-]+)*|"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d',
      '-\\x7f]|',
      '\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]',
      '*[a-z0-9])',
      '?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|',
      '[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|',
      '[a-z0-9-]',
      '*[a-z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|',
      '\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])$',
    ].join(''),
  );

  return re.test(String(email).toLowerCase().replace(/\s/g, ''));
}

// /////////////////////////////////////////////////////////
//
// Exportação
//
// /////////////////////////////////////////////////////////

export { isValidCEP, isValidCPF, isValidCNPJ, isValidPassword, isValidEmail };
